import "ag-grid-enterprise";
import React, { useMemo } from "react";
import { Badge, Box, Group, Pagination, Select, Stack, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-enterprise";
import { useSelector } from "react-redux";
import { openapi, type PostMultiplacerDrofusInterfaceArticlesApiResponse } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAgGridStateByKey } from "../agGridStateSlice";
import { useAgGridReduxState } from "../hooks/useAgGridReduxState";
import { useProjectId } from "../hooks/useProjectId";
import {
  selectDrofusForekomsterArticleLimit,
  selectDrofusForekomsterArticlesApiArg,
  setDrofusForekomsterArticleLimit,
  setDrofusForekomsterArticleOffset,
} from "./drofusForekomsterSlice";

type DrofusArticleRow = PostMultiplacerDrofusInterfaceArticlesApiResponse["items"][number];

const disciplineColorById: Record<string, string> = {
  RIE: "#f59f00",
  RIKT: "#1971c2",
  RIAut: "#9c36b5",
  RIV: "#0c8599",
};

const defaultColDef: ColDef<DrofusArticleRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  enableRowGroup: false,
  enablePivot: false,
  enableValue: false,
  minWidth: 120,
};

const columnsOnlySideBar = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Kolonner",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      },
    },
  ],
};

function formatValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

const DisciplineBadgeRenderer: React.FC<ICellRendererParams<DrofusArticleRow, string[]>> = ({
  value,
}) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  return (
    <Group gap={4} wrap="nowrap">
      {value.map((discipline) => (
        <Badge
          key={discipline}
          size="sm"
          radius="sm"
          styles={{
            root: {
              backgroundColor: "#ffffff",
              border: `1px solid ${disciplineColorById[discipline] ?? "#868e96"}`,
              color: disciplineColorById[discipline] ?? "#495057",
            },
            label: { color: "inherit" },
          }}
        >
          {discipline}
        </Badge>
      ))}
    </Group>
  );
};

const NeedsBadgeRenderer: React.FC<ICellRendererParams<DrofusArticleRow>> = ({ data }) => {
  if (!data) return null;

  const needs = [
    data.needs.electrical ? "Elektro" : null,
    data.needs.ikt ? "IKT" : null,
    data.needs.automation ? "Aut" : null,
    data.needs.vvs ? "VVS" : null,
  ].filter(Boolean);

  if (needs.length === 0) return <Text size="sm" c="dimmed">-</Text>;

  return (
    <Group gap={4} wrap="nowrap">
      {needs.map((need) => (
        <Badge key={need} size="sm" radius="sm" variant="light" color="gray">
          {need}
        </Badge>
      ))}
    </Group>
  );
};

export const DrofusArticlesTable: React.FC = () => {
  const projectNo = useProjectId();
  const dispatch = useAppDispatch();
  const articlesApiArg = useAppSelector(selectDrofusForekomsterArticlesApiArg);
  const configuredLimit = useAppSelector(selectDrofusForekomsterArticleLimit);

  const gridKey = `drofusArticlesTableNoLocalOps:${projectNo}`;
  const saved = useSelector(selectAgGridStateByKey(gridKey));
  const hasSavedCols = !!(saved?.columnState && saved.columnState.length > 0);

  const { data, isLoading, isFetching, isError } =
    openapi.endpoints.postMultiplacerDrofusInterfaceArticles.useQuery(articlesApiArg ?? skipToken);

  const rowData = data?.items ?? [];
  const totalRows = data?.total ?? 0;
  const limit = data?.limit ?? articlesApiArg?.body.limit ?? configuredLimit;
  const offset = data?.offset ?? articlesApiArg?.body.offset ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / limit));
  const activePage = Math.floor(offset / limit) + 1;
  const firstVisibleRow = totalRows === 0 ? 0 : offset + 1;
  const lastVisibleRow = Math.min(offset + rowData.length, totalRows);

  const allColIds = useMemo(
    () => [
      "articleId",
      "compCode",
      "articleName",
      "articleResponsibility",
      "occurrenceCount",
      "uniqueRoomCount",
      "totalQuantity",
      "affectedDisciplines",
      "interfaceRelevantCount",
      "needs",
      "toBeDrawn",
      "bip",
    ],
    []
  );
  const columnsSignature = useMemo(() => `noLocalOps:v2:${allColIds.join("|")}`, [allColIds]);

  const { gridStateHandlers } = useAgGridReduxState(gridKey, {
    availableColIds: allColIds,
    columnsSignature,
    managePivotMode: false,
  });

  const columnDefs = useMemo<ColDef<DrofusArticleRow>[]>(
    () => [
      {
        headerName: "Artikkel ID",
        field: "articleId",
        colId: "articleId",
        width: 120,
      },
      {
        headerName: "CompCode",
        field: "compCode",
        colId: "compCode",
        width: 150,
        pinned: "left",
      },
      {
        headerName: "Artikkelnavn",
        field: "articleName",
        colId: "articleName",
        flex: 1,
        minWidth: 280,
        pinned: "left",
      },
      {
        headerName: "Ansvar",
        field: "articleResponsibility",
        colId: "articleResponsibility",
        width: 120,
      },
      {
        headerName: "Forekomster",
        field: "occurrenceCount",
        colId: "occurrenceCount",
        width: 130,
      },
      {
        headerName: "Unike rom",
        field: "uniqueRoomCount",
        colId: "uniqueRoomCount",
        width: 130,
      },
      {
        headerName: "Total antall",
        field: "totalQuantity",
        colId: "totalQuantity",
        width: 130,
      },
      {
        headerName: "Fag",
        field: "affectedDisciplines",
        colId: "affectedDisciplines",
        width: 180,
        cellRenderer: "DisciplineBadgeRenderer",
        valueFormatter: ({ value }: ValueFormatterParams<DrofusArticleRow, string[]>) =>
          Array.isArray(value) ? value.join(", ") : "",
      },
      {
        headerName: "Relevant forekomster",
        field: "interfaceRelevantCount",
        colId: "interfaceRelevantCount",
        width: 170,
      },
      {
        headerName: "Behov",
        colId: "needs",
        width: 220,
        cellRenderer: "NeedsBadgeRenderer",
      },
      {
        headerName: "Skal modelleres",
        field: "toBeDrawn",
        colId: "toBeDrawn",
        width: 140,
        valueFormatter: ({ value }) => formatValue(value),
      },
      {
        headerName: "BIP",
        field: "bip",
        colId: "bip",
        width: 100,
        valueFormatter: ({ value }) => formatValue(value),
      },
    ],
    []
  );

  return (
    <Stack h="100%" w="100%" gap="sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {isError
              ? "Kunne ikke laste artikler"
              : articlesApiArg
                ? `${firstVisibleRow}-${lastVisibleRow} av ${totalRows}`
                : "Velg en kjøring"}
          </Text>
          {isFetching && (
            <Badge variant="light" color="blue" radius="sm">
              Laster
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
          Gruppert på artikkel
        </Text>
      </Group>

      <Box className="ag-theme-quartz" style={{ flex: 1, minHeight: 0, width: "100%" }}>
        <AgGridReact<DrofusArticleRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={isLoading}
          animateRows
          tooltipShowDelay={150}
          autoSizeStrategy={
            hasSavedCols ? undefined : { type: "fitGridWidth", defaultMinWidth: 120 }
          }
          headerHeight={56}
          rowGroupPanelShow="never"
          suppressScrollOnNewData
          sideBar={columnsOnlySideBar}
          suppressRowGroupHidesColumns
          suppressAggFuncInHeader
          pivotMode={false}
          components={{
            DisciplineBadgeRenderer,
            NeedsBadgeRenderer,
          }}
          {...gridStateHandlers}
          overlayNoRowsTemplate={
            articlesApiArg
              ? "<span style='padding:8px;display:inline-block;'>Ingen artikler funnet</span>"
              : "<span style='padding:8px;display:inline-block;'>Ingen kjøring valgt</span>"
          }
        />
      </Box>

      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Rader per side
          </Text>
          <Select
            size="xs"
            w={90}
            value={String(limit)}
            data={["50", "100", "250", "500"]}
            disabled={!articlesApiArg || isFetching}
            onChange={(value) => {
              const nextLimit = Number(value);
              if (Number.isFinite(nextLimit) && nextLimit > 0) {
                dispatch(setDrofusForekomsterArticleLimit(nextLimit));
              }
            }}
          />
        </Group>
        <Pagination
          size="sm"
          total={totalPages}
          value={activePage}
          disabled={!articlesApiArg || isFetching}
          onChange={(page) => dispatch(setDrofusForekomsterArticleOffset((page - 1) * limit))}
        />
      </Group>
    </Stack>
  );
};
